import { notEmpty } from "@product/scmp-sdk";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import type { homeSecondaryContentItemContent$key } from "~/queries/__generated__/homeSecondaryContentItemContent.graphql";
import type { CustomQueryParameters } from "~/types/gtm";

import {
  ActionBar,
  Container,
  CoverImage,
  Headline,
  Section,
  StyledCoverEntityLink,
  StyledEntityLink,
  Summary,
} from "./styles";

export type Props = {
  className?: string;
  customQueryParameters?: CustomQueryParameters;
  provider: DefaultComponentsProvider;
  reference: homeSecondaryContentItemContent$key;
  variantProps?: ContentItemHomeSecondaryVariantProps;
};

export type ContentItemHomeSecondaryVariantProps = {
  withImage?: boolean;
  withSection?: boolean;
  withSummary?: boolean;
};

export const ContentItemHomeSecondary = forwardRef<HTMLDivElement, Props>(
  (
    { className, customQueryParameters, provider, reference: reference_, variantProps },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment homeSecondaryContentItemContent on Content {
          ...entityLink
        }
      `,
      reference_,
    );

    const isSponsorArticle = notEmpty(provider.sponsorTag?.());

    return (
      <Container className={className} ref={reference}>
        {variantProps?.withSection && <Section>{provider.sections()}</Section>}
        <StyledEntityLink query={customQueryParameters} reference={content}>
          <Headline>{provider.headline()}</Headline>
        </StyledEntityLink>
        {variantProps?.withSummary && !isSponsorArticle && <Summary>{provider.summary()}</Summary>}

        {isSponsorArticle ? (
          provider.sponsorTag?.({ variant: "home" })
        ) : (
          <ActionBar>
            {provider.actionBar({
              isBookmarkVisible: false,
              isCommentsVisible: false,
            })}
          </ActionBar>
        )}
        {variantProps?.withImage && (
          <StyledCoverEntityLink $isSponsorArticle={isSponsorArticle} reference={content}>
            <CoverImage>
              {provider.coverImage({
                responsiveVariants: {
                  desktopUp: "size320x240",
                  homeDesktopUp: "size320x240",
                  mobileUp: "size320x240",
                  tabletUp: "size320x240",
                },
              })}
            </CoverImage>
          </StyledCoverEntityLink>
        )}
      </Container>
    );
  },
);

ContentItemHomeSecondary.displayName = "ContentItemHomeSecondary";
